import { parseThemeColors } from '../gameTemplates';

export const dragAndDropTemplate = (data) => {
  const { title, items, themeColors, themeFonts, backgroundImage } = data;
  const colors = parseThemeColors(themeColors);
  
  return `const InteractiveElement = () => {
  const [currentScreen, setCurrentScreen] = React.useState('start');
  const [matchingPairs, setMatchingPairs] = React.useState([]);
  const [droppedItems, setDroppedItems] = React.useState({});
  const [shuffledItems, setShuffledItems] = React.useState([]);
  const [startTime, setStartTime] = React.useState(null);
  const traceId = React.useRef(null);
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const [draggedItem, setDraggedItem] = React.useState(null);
  const [dragOverTarget, setDragOverTarget] = React.useState(null);
  const [moveCount, setMoveCount] = React.useState(0);

  React.useEffect(() => {
    const items = ${JSON.stringify(items, null, 2)};
    const pairs = items.map(item => ({
      id: String(item.id),
      item: item.item,
      target: item.correctZone,
      explanation: item.explanation
    }));
    setMatchingPairs(pairs);
    setShuffledItems([...pairs].sort(() => Math.random() - 0.5));
  }, []);

  // Update pastel colors to use more neutral tones
  const neutralPastelColors = [
    { bg: 'bg-[#FFE5F1]', border: 'border-[#FFB3D9]', text: 'text-[#990066]' }, // Baby pink
    { bg: 'bg-[#E5F3FF]', border: 'border-[#B3D9FF]', text: 'text-[#0066CC]' }, // Soft blue
    { bg: 'bg-[#F5E5FF]', border: 'border-[#D9B3FF]', text: 'text-[#6600CC]' }, // Soft purple
    { bg: 'bg-[#E6FFB3]', border: 'border-[#CCE699]', text: 'text-[#4D7300]' }, // Yellow lime
    { bg: 'bg-[#F5E6D3]', border: 'border-[#E6C4A3]', text: 'text-[#8B4513]' }, // beige
  ];

  const getColorForItem = (itemId) => {
    const colorIndex = (parseInt(itemId) - 1) % neutralPastelColors.length;
    return neutralPastelColors[colorIndex];
  };

  React.useEffect(() => {
    let interval;
    if (currentScreen === 'playing' && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime.getTime()) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentScreen, startTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return \`\${mins}:\${secs.toString().padStart(2, '0')}\`;
  };

  const ConfettiPiece = ({ x, y, color, shape, delay }) => {
    const spring = useSpring({
      from: { y: -20, opacity: 1, rotate: 0, scale: 0 },
      to: async (next) => {
        await next({ y: y + Math.random() * 100, opacity: 1, rotate: 360, scale: 1, config: { duration: 1000 } });
        await next({ y: y + 200, opacity: 0, config: { duration: 1000 } });
      },
      config: { mass: 1, tension: 120, friction: 30 },
      delay: delay * 1000,
    });

    return (
      <animated.div
        style={{
          position: 'absolute',
          left: \`\${x}%\`,
          top: \`\${y}%\`,
          transform: spring.rotate.to(r => \`rotate(\${r}deg) scale(\${spring.scale.get()})\`),
          y: spring.y,
          opacity: spring.opacity,
        }}
      >
        <span style={{ color, fontSize: '2rem', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))' }}>
          {shape}
        </span>
      </animated.div>
    );
  };

  const Confetti = () => {
    const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD'];
    const shapes = ['★', '●', '♦', '♠', '♥', '♣', '✦', '✧'];
    
    const confettiItems = useMemo(() => Array.from({ length: 100 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 50,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      delay: Math.random() * 3
    })), []);

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        {confettiItems.map((item) => (
          <ConfettiPiece key={item.id} {...item} />
        ))}
      </div>
    );
  };

  const handleDragStart = (e, itemId) => {
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(itemId));
      setDraggedItem(String(itemId));
    } catch (error) {
      console.warn('Drag start failed:', error);
      setDraggedItem(String(itemId));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    
    let itemId;
    try {
      itemId = e.dataTransfer.getData('text/plain');
    } catch (error) {
      itemId = draggedItem;
    }
    
    if (!itemId && draggedItem) {
      itemId = draggedItem;
    }
    
    if (itemId) {
      const newDroppedItems = { ...droppedItems };
      Object.keys(newDroppedItems).forEach(key => {
        if (newDroppedItems[key] === itemId) {
          delete newDroppedItems[key];
        }
      });
      
      newDroppedItems[String(targetId)] = String(itemId);
      setDroppedItems(newDroppedItems);
      setMoveCount(prev => prev + 1);
    }
    
    setDraggedItem(null);
    setDragOverTarget(null);
  };

  const handleCheckAnswers = () => {
    setCurrentScreen('summary');
  };

  const handleBegin = () => {
    const newTraceId = \`interactive_\${Date.now()}_\${Math.random().toString(36).substr(2, 9)}\`;
    traceId.current = newTraceId;
    const timestamp = Date.now();
    
    setStartTime(new Date());
    setCurrentScreen('playing');
    setElapsedTime(0);
    setMoveCount(0);
    
    if (typeof trackEvent === 'function') {
      trackEvent('interactive_start', newTraceId, {
        startTime: timestamp,
        status: 'started'
      });
    }
  };

  const handleRestart = () => {
    setCurrentScreen('start');
    setDroppedItems({});
    setStartTime(null);
    setElapsedTime(0);
    setMoveCount(0);
    setDraggedItem(null);
    setDragOverTarget(null);
    
    setShuffledItems([...matchingPairs].sort(() => Math.random() - 0.5));
  };

  return (
    <div className="relative min-h-screen bg-cover bg-center bg-fixed" 
         style={{
        backgroundImage: 'url(${backgroundImage})',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        fontFamily: '${themeFonts}'
      }}>
      <div className="relative z-10 min-h-screen flex items-center justify-center">
        {/* Screen 1: Start */}
        {currentScreen === 'start' && (
          <div className="w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="flex flex-col items-center justify-center gap-10 py-24 px-6">
              <h1 className="text-[${colors.primary}] text-[32px] font-bold leading-[150%] tracking-[-0.022rem] text-center">
                ${title}
              </h1>
              <p className="text-[#4E4E4E] text-[24px] font-medium leading-[150%] tracking-[-0.0165rem] text-center">
                Drag items to their matching definitions!
              </p>
              <button
                onClick={handleBegin}
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[${colors.primary}] hover:bg-[${colors.primary}90] text-white transition-all transform hover:scale-105 active:scale-95"
              >
                <span className="text-[20px] font-semibold">Begin</span>
              </button>
            </div>
          </div>
        )}

        {/* Screen 2: Playing */}
        {currentScreen === 'playing' && (
          <div className="w-[950px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="p-10">
              <h2 className="text-[${colors.primary}] text-[32px] font-bold tracking-[-0.022rem] text-center mb-4">
                Match the Items
              </h2>
              
              {/* Timer and Progress Display */}
              <div className="flex justify-between items-center mb-8 px-4">
                <div className="text-[${colors.primary}] text-lg">
                  Time: <span className="font-bold">{formatTime(elapsedTime)}</span>
                </div>
                <div className="text-[${colors.primary}] text-lg">
                  Progress: <span className="font-bold">{Object.keys(droppedItems).length}/{matchingPairs.length}</span>
                </div>
                <div className="text-[${colors.primary}] text-lg">
                  Moves: <span className="font-bold">{moveCount}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-10">
                {/* Left column - Draggable Items */}
                <div>
                  <div className="bg-[#F8F7FC] rounded-xl p-6 min-h-[400px]">
                    <h3 className="text-[${colors.primary}] text-[20px] font-semibold mb-6 text-center">Items</h3>
                    <div className="space-y-3">
                      {shuffledItems.map((item) => {
                        const droppedTargetId = Object.entries(droppedItems).find(
                          ([_, itemId]) => itemId === item.id
                        )?.[0];
                        const isDropped = droppedTargetId !== undefined;
                        const isDragging = draggedItem === item.id;
                        const isHoveringTarget = isDragging && dragOverTarget !== null;
                        const colorScheme = isDropped ? getColorForItem(item.id) : null;
                        
                        return (
                          <div
                            key={item.id}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, item.id)}
                            onDragEnd={() => setDraggedItem(null)}
                            className={\`min-h-[60px] flex items-center justify-center px-3 py-2 rounded-lg text-center cursor-move transition-all transform \${
                              isDropped
                                ? \`\${colorScheme.bg} \${colorScheme.border} border-2\`
                                : isDragging
                                ? isHoveringTarget
                                  ? 'bg-[${colors.secondary}] text-white scale-105 shadow-lg'
                                  : 'bg-[#E0DEEF] scale-105 shadow-lg'
                                : draggedItem === item.id
                                ? 'bg-white border-2 border-[#E0DEEF] ring-4 ring-[${colors.accent}] ring-offset-2'
                                : 'bg-white border-2 border-[#E0DEEF] hover:border-[${colors.accent}] hover:shadow-md'
                            }\`}
                          >
                            <p className={\`text-[16px] select-none leading-tight \${
                              isDropped
                                ? colorScheme.text
                                : isDragging && isHoveringTarget 
                                ? 'text-white' 
                                : 'text-[#4E4E4E]'
                            }\`}
                               dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.item) }}>
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right column - Drop Zones */}
                <div>
                  <div className="bg-[#F8F7FC] rounded-xl p-6 min-h-[400px]">
                    <h3 className="text-[${colors.primary}] text-[20px] font-semibold mb-6 text-center">Definitions</h3>
                    <div className="space-y-3">
                      {matchingPairs.map((pair) => {
                        const droppedItemId = droppedItems[String(pair.id)];
                        const droppedItem = droppedItemId ? matchingPairs.find(p => String(p.id) === droppedItemId) : null;
                        const isDragOver = dragOverTarget === pair.id;
                        const colorScheme = droppedItem ? getColorForItem(droppedItemId) : null;
                        
                        return (
                          <div
                            key={pair.id}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => {
                              e.preventDefault();
                              setDragOverTarget(String(pair.id));
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              setDragOverTarget(null);
                            }}
                            onDrop={(e) => handleDrop(e, pair.id)}
                            className={\`min-h-[60px] flex items-center justify-center px-4 py-3 rounded-lg transition-all \${
                              droppedItem
                                ? \`\${colorScheme.bg} \${colorScheme.border} border-2\`
                                : isDragOver && draggedItem
                                ? 'bg-[${colors.secondary}] text-white scale-105 shadow-lg'
                                : 'bg-white border-2 border-dashed border-[#D0D0D0] hover:border-[${colors.accent}]'
                            }\`}
                          >
                            {!droppedItem ? (
                              <p className={\`text-[16px] leading-tight text-center \${
                                isDragOver && draggedItem ? 'text-white' : 'text-[#4E4E4E]'
                              }\`}
                                 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pair.target) }}>
                              </p>
                            ) : (
                              <div className="text-center">
                                <p className={\`text-[14px] leading-tight \${colorScheme.text}\`}
                                   dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pair.target) }}>
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-8">
                <button
                  onClick={handleCheckAnswers}
                  disabled={Object.keys(droppedItems).length !== matchingPairs.length}
                  className={\`inline-flex items-center justify-center px-8 py-3 rounded-lg text-white transition-all transform \${
                    Object.keys(droppedItems).length !== matchingPairs.length
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-[${colors.primary}] hover:bg-[${colors.primary}90] hover:scale-105 active:scale-95'
                  }\`}
                >
                  <span className="text-[20px] font-semibold">Check Answers</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Screen 3: Summary */}
        {currentScreen === 'summary' && (() => {
          // Count correct matches by checking if each dropped item matches its target pair
          const correctCount = matchingPairs.filter(pair => {
            const droppedItemId = droppedItems[String(pair.id)];
            return droppedItemId === String(pair.id);
          }).length;
          
          const scorePercentage = Math.round((correctCount / matchingPairs.length) * 100);
          const endTime = Date.now();
          const totalTime = startTime ? Math.floor((endTime - startTime.getTime()) / 1000) : 0;

          if (traceId.current) {
            if (typeof trackEvent === 'function') {
              trackEvent('interactive_end', traceId.current, {
                totalScore: matchingPairs.length,
                timeTaken: totalTime,
                userScore: correctCount,
                endTime: endTime,
                status: 'completed'
              });
            }
            traceId.current = null;
          }

          return (
            <div className="w-[950px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
              {scorePercentage === 100 && <Confetti />}
              
              <div className="p-6">
                <h2 className="text-[${colors.primary}] text-[28px] font-bold tracking-[-0.022rem] text-center mb-4">
                  Your Results
                </h2>

                {/* Compact Score Summary */}
                <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-4 rounded-xl text-center mb-6 max-w-md mx-auto">
                  <div className="flex items-center justify-around">
                    <div>
                      <div className="text-[#515151] text-sm">Score</div>
                      <div className="text-[${colors.primary}] text-2xl font-bold">{scorePercentage}%</div>
                    </div>
                    <div className="h-12 w-[1px] bg-[#E0DEEF]"></div>
                    <div>
                      <div className="text-[#515151] text-sm">Time</div>
                      <div className="text-[${colors.primary}] text-2xl font-bold">{formatTime(totalTime)}</div>
                    </div>
                    <div className="h-12 w-[1px] bg-[#E0DEEF]"></div>
                    <div>
                      <div className="text-[#515151] text-sm">Moves</div>
                      <div className="text-[${colors.primary}] text-2xl font-bold">{moveCount}</div>
                    </div>
                  </div>
                </div>

                {/* Compact Correct Answers with Explanations */}
                <div className="mb-6">
                  <h3 className="text-[${colors.primary}] text-[20px] font-semibold mb-3 text-center">Correct Matching & Explanations</h3>
                  <div className="bg-white rounded-lg p-4 border border-[#E0DEEF]">
                    <div className="space-y-3">
                      {matchingPairs.map((pair) => {
                        const droppedItemId = droppedItems[String(pair.id)];
                        const isCorrect = droppedItemId === String(pair.id);
                        const droppedItem = droppedItemId ? matchingPairs.find(p => String(p.id) === droppedItemId) : null;
                        
                        return (
                          <div key={pair.id} className="border-b border-[#E0DEEF] last:border-b-0 pb-3 last:pb-0">
                            <div className="flex items-start gap-2">
                              <span className={\`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold flex-shrink-0 \${
                                isCorrect ? 'bg-green-500' : 'bg-red-500'
                              }\`}>
                                {isCorrect ? '✓' : '✗'}
                              </span>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-[${colors.primary}] font-medium text-lg"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pair.item) }}>
                                  </span>
                                  <span className="text-[#8C8C9D] text-md">→</span>
                                  <span className="text-[#4E4E4E] text-lg"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pair.target) }}>
                                  </span>
                                </div>
                                <p className="text-[#6E6E7C] italic text-md"
                                   dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pair.explanation) }}>
                                </p>
                                {!isCorrect && droppedItem && (
                                  <p className="text-red-600 text-base mt-0.5"
                                     dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(\`Your answer: \${droppedItem.target}\`) }}>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button 
                    onClick={handleRestart} 
                    className="inline-flex items-center justify-center px-8 py-2.5 rounded-lg bg-[${colors.primary}] hover:bg-[${colors.primary}90] text-white transition-all transform hover:scale-105 active:scale-95"
                  >
                    <span className="text-[18px] font-semibold">Retry</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};`;
};
